import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import "../styles/Noticias.css";

const Noticias = () => {
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [noticiasPerPage] = useState(5);
  const [selectedNoticia, setSelectedNoticia] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // Nuevo estado para imagen seleccionada
  const [showImageModal, setShowImageModal] = useState(false); // Nuevo estado para modal de imagen
  const location = useLocation();

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.PUBLIC_URL}/data/noticias.json`,
        );
        if (!response.ok) {
          throw new Error("Error al cargar las noticias");
        }
        const data = await response.json();
        setNoticias(data);
      } catch (err) {
        setError(err.message);
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching noticias:", err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, []);

  // Auto-open news article if ID in URL
  useEffect(() => {
    if (noticias.length > 0 && location.search) {
      const params = new URLSearchParams(location.search);
      const noticiaId = parseInt(params.get("id"));
      if (noticiaId) {
        const noticia = noticias.find((n) => n.id === noticiaId);
        if (noticia) {
          setSelectedNoticia(noticia);
          setShowModal(true);
        }
      }
    }
  }, [noticias, location.search]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("es-ES", options);
  };

  const handleLeerMas = (noticia) => {
    setSelectedNoticia(noticia);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNoticia(null);
  };

  // Modificar la función handleImageClick para pasar también el índice
  const handleImageClick = (imagen, index) => {
    setSelectedImage({ imagen, index });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  // Ordenar noticias por fecha descendente (más recientes primero)
  const sortedNoticias = [...noticias].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha),
  );

  // Calcular noticias para la página actual
  const indexOfLastNoticia = currentPage * noticiasPerPage;
  const indexOfFirstNoticia = indexOfLastNoticia - noticiasPerPage;
  const currentNoticias = sortedNoticias.slice(
    indexOfFirstNoticia,
    indexOfLastNoticia,
  );

  // Cambiar página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Obtener números de página para mostrar
  const getPageNumbers = () => {
    const totalPages = Math.ceil(sortedNoticias.length / noticiasPerPage);
    const pageNumbers = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="noticias-loading">
        <div className="loading-spinner"></div>
        <p>Cargando noticias...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="noticias-error">
        <p>Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="presentation-page pt-16">
      {/* Hero Section */}
      <header className="noticias-hero-section">
        <div className="noticias-hero-content">
          <div className="noticias-hero-container">
            {/* Hero Card */}
            <div className="noticias-hero-card">
              <div className="noticias-hero-grid">
                <div className="noticias-hero-left">
                  <h1 className="noticias-hero-title">
                    <span className="noticias-hero-title-gradient">
                      Noticias
                    </span>{" "}
                    y Eventos de
                    <span className="noticias-hero-title-gradient">
                      {" "}
                      RESNOM
                    </span>
                  </h1>
                </div>
                <div className="noticias-hero-right">
                  <p className="noticias-hero-description">
                    Mantente informado sobre las últimas noticias, eventos y
                    desarrollos de la Red Sísmica del Noroeste de México.
                    Descubre los avances tecnológicos, colaboraciones
                    internacionales y mejoras en nuestro sistema de monitoreo
                    sísmico.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Noticias Section */}
      <section className="noticias-content-section py-sm-7 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="noticias-header">
                <div className="noticias-stats">
                  <span className="noticias-total">
                    Total: {sortedNoticias.length} noticias
                  </span>
                  <span className="noticias-showing">
                    Mostrando {indexOfFirstNoticia + 1}-
                    {Math.min(indexOfLastNoticia, sortedNoticias.length)} de{" "}
                    {sortedNoticias.length}
                  </span>
                </div>
              </div>

              {sortedNoticias.length === 0 ? (
                <div className="noticias-no-data">
                  <p>No hay noticias disponibles en este momento.</p>
                </div>
              ) : (
                <>
                  {/* Lista de Noticias */}
                  <div className="noticias-list">
                    {currentNoticias.map((noticia) => {
                      // Determinar URL de thumbnail con fallback inteligente
                      const getThumbnailUrl = () => {
                        // 1º Prioridad: Campo thumbnail específico
                        if (noticia.thumbnail) {
                          return `${process.env.PUBLIC_URL}/assets/img/noticias/${noticia.thumbnail}`;
                        }
                        // 2º Prioridad: Primera imagen en galería
                        if (noticia.imagenes && noticia.imagenes.length > 0) {
                          return `${process.env.PUBLIC_URL}/assets/img/noticias/${noticia.imagenes[0]}`;
                        }
                        // 3º Prioridad: Imagen por defecto
                        return `${process.env.PUBLIC_URL}/assets/img/noticias/noticia-thumbnail-default.webp`;
                      };

                      return (
                        <article key={noticia.id} className="noticia-list-item">
                          <div className="noticia-list-image">
                            <img
                              src={getThumbnailUrl()}
                              alt={noticia.titulo}
                              className="noticia-list-img"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = `${process.env.PUBLIC_URL}/assets/img/noticias/noticia-thumbnail-default.webp`;
                                e.target.alt = noticia.titulo;
                              }}
                            />
                          </div>

                          <div className="noticia-list-content">
                            <div className="noticia-list-meta">
                              <span className="noticia-list-fecha">
                                {formatDate(noticia.fecha)}
                              </span>
                              <span className="noticia-list-id">
                                #{noticia.id}
                              </span>
                            </div>

                            <h3 className="noticia-list-titulo">
                              {noticia.titulo}
                            </h3>

                            <p className="noticia-list-resumen">
                              {noticia.resumen}
                            </p>

                            <div className="noticia-list-actions">
                              <button
                                className="noticia-btn-primary"
                                onClick={() => handleLeerMas(noticia)}
                              >
                                Leer más
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {/* Paginación */}
                  {sortedNoticias.length > noticiasPerPage && (
                    <div className="noticias-pagination">
                      <div className="pagination-info">
                        <span>
                          Página {currentPage} de{" "}
                          {Math.ceil(sortedNoticias.length / noticiasPerPage)}
                        </span>
                      </div>

                      <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          ← Anterior
                        </button>

                        <div className="pagination-numbers">
                          {getPageNumbers().map((number, index) => (
                            <button
                              key={index}
                              className={`pagination-number ${number === currentPage ? "active" : ""} ${number === "..." ? "disabled" : ""}`}
                              onClick={() =>
                                typeof number === "number" && paginate(number)
                              }
                              disabled={number === "..."}
                            >
                              {number}
                            </button>
                          ))}
                        </div>

                        <button
                          className="pagination-btn"
                          onClick={() => paginate(currentPage + 1)}
                          disabled={
                            currentPage ===
                            Math.ceil(sortedNoticias.length / noticiasPerPage)
                          }
                        >
                          Siguiente →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Noticia Completa */}
      {showModal && selectedNoticia && (
        <div className="noticia-modal-overlay" onClick={closeModal}>
          <div className="noticia-modal" onClick={(e) => e.stopPropagation()}>
            <div className="noticia-modal-header">
              <h2 className="noticia-modal-title">{selectedNoticia.titulo}</h2>
              <button className="noticia-modal-close" onClick={closeModal}>
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="noticia-modal-content">
              <div className="noticia-modal-meta">
                <span className="noticia-modal-fecha">
                  {formatDate(selectedNoticia.fecha)}
                </span>
                <span className="noticia-modal-id">#{selectedNoticia.id}</span>
              </div>
              <div className="noticia-modal-body">
                <div className="noticia-modal-resumen">
                  <ReactMarkdown
                    components={{
                      img: ({ node, ...props }) => (
                        <img
                          {...props}
                          src={
                            props.src?.startsWith("http")
                              ? props.src
                              : `${process.env.PUBLIC_URL}/assets/img/noticias/${props.src}`
                          }
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "10px",
                            margin: "1rem 0",
                          }}
                          alt={props.alt || "Imagen de la noticia"}
                        />
                      ),
                    }}
                  >
                    {selectedNoticia.noticia}
                  </ReactMarkdown>
                </div>
              </div>
              {selectedNoticia.imagenes &&
                selectedNoticia.imagenes.length > 1 && (
                  <div className="noticia-modal-gallery">
                    <h4>Galería de Imágenes</h4>
                    <div className="noticia-modal-gallery-grid">
                      {selectedNoticia.imagenes.map((imagen, index) => (
                        <div key={index} className="noticia-modal-gallery-item">
                          <div className="gallery-image-container">
                            <img
                              src={`${process.env.PUBLIC_URL}/assets/img/noticias/${imagen}`}
                              alt={`${selectedNoticia.titulo} - Imagen ${index + 1}`}
                              onClick={() => handleImageClick(imagen, index)}
                              onError={(e) => {
                                e.target.src = `${process.env.PUBLIC_URL}/assets/img/placeholder-noticia.jpg`;
                                e.target.alt = "Imagen no disponible";
                              }}
                            />
                          </div>
                          {selectedNoticia.descripcionImg &&
                            selectedNoticia.descripcionImg[index] && (
                              <div className="gallery-image-description">
                                <p>{selectedNoticia.descripcionImg[index]}</p>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="noticia-modal-footer">
              <button className="noticia-modal-btn" onClick={closeModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Imagen Ampliada */}
      {showImageModal && selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div className="image-modal-content">
              <img
                src={`${process.env.PUBLIC_URL}/assets/img/noticias/${selectedImage.imagen}`}
                alt="Imagen ampliada"
                className="image-modal-img"
                onError={(e) => {
                  e.target.src = `${process.env.PUBLIC_URL}/assets/img/placeholder-noticia.jpg`;
                  e.target.alt = "Imagen no disponible";
                }}
              />
              {selectedNoticia.descripcionImg &&
                selectedNoticia.descripcionImg[selectedImage.index] && (
                  <div className="image-modal-description">
                    <p>{selectedNoticia.descripcionImg[selectedImage.index]}</p>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Noticias;
